import { Box, Button, Spinner, Text } from '@chakra-ui/react'
import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '~/components/ui/tooltip'
import {
  StartCallsExportParams,
  useCallsExport,
} from '~/providers/CallsExportProvider'

import { textStyleKeys, textStyles } from '@mangovoice/design-system'
import {
  CallDirection,
  CallFilterParams,
  CallsQueryParams,
} from '@mangovoice/types'

import {
  CallsViewModelState,
  useCallsViewModelContext,
} from './Calls.viewmodel'

const DownloadIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const statusLabelKey = {
  idle: 'calls.export.button',
  starting: 'calls.export.starting',
  pending: 'calls.export.preparing',
  processing: 'calls.export.processing',
  completed: 'calls.export.completed',
  failed: 'calls.export.button',
} as const

type Filters = CallsViewModelState &
  Partial<CallFilterParams> &
  Partial<CallsQueryParams>

type TFunction = ReturnType<typeof useTranslation>['t']

function getUnsupportedFilterNames(filters: Filters, t: TFunction): string[] {
  const names: string[] = []

  const rawDirections = filters.directions as
    CallDirection[] | string | undefined
  const directions = Array.isArray(rawDirections)
    ? rawDirections
    : typeof rawDirections === 'string'
      ? rawDirections.split(',')
      : undefined

  if (directions?.includes('internal')) {
    names.push(t('callFilters.direction.internal'))
  } else if (directions && directions.length > 1) {
    names.push(t('callFilters.sections.direction'))
  }

  if (filters.search) {
    names.push(t('calls.export.filterSearch'))
  }

  if (
    filters.duration_in_seconds_gte !== undefined ||
    filters.duration_in_seconds_lt !== undefined
  ) {
    names.push(t('callFilters.sections.duration'))
  }

  if (filters.statuses && filters.statuses.length > 0) {
    names.push(t('callFilters.sections.status'))
  }

  if (
    (filters.office_overall_sentiment_score &&
      filters.office_overall_sentiment_score.length > 0) ||
    (filters.customer_overall_sentiment_score &&
      filters.customer_overall_sentiment_score.length > 0)
  ) {
    names.push(t('callFilters.sections.sentiment'))
  }

  if (filters.related_user_ids && filters.related_user_ids.length > 0) {
    names.push(t('callFilters.sections.user'))
  }

  if (filters.is_externally_transferred !== undefined) {
    names.push(t('callFilters.callTrait.externallyTransferred'))
  }

  return names
}

export const ExportCallsCsvButton = () => {
  const { t } = useTranslation()
  const { state } = useCallsViewModelContext()
  const { status, isExporting, startExport, reset } = useCallsExport()

  const filters = state as Filters

  const unsupportedFilterNames = getUnsupportedFilterNames(filters, t)
  const hasUnsupportedFilters = unsupportedFilterNames.length > 0

  // Reset export state when filters change so the button becomes actionable again
  const prevDateRangeRef = useRef(state.dateTimeRange)
  useEffect(() => {
    const prev = prevDateRangeRef.current
    const curr = state.dateTimeRange
    if (
      status !== 'idle' &&
      !isExporting &&
      (prev?.after?.getTime() !== curr?.after?.getTime() ||
        prev?.before?.getTime() !== curr?.before?.getTime())
    ) {
      reset()
    }
    prevDateRangeRef.current = curr
  }, [state.dateTimeRange, status, isExporting, reset])

  const handleClick = useCallback(() => {
    if (isExporting) return

    const startDate = state.dateTimeRange?.after
    const endDate = state.dateTimeRange?.before
    if (!startDate || !endDate) return

    const params: StartCallsExportParams = {
      startDate,
      endDate,
    }

    // Viewmodel spreads URL params with `as CallDirection[]` — runtime value
    // is a plain string when a single direction is selected.
    const rawDirections = filters.directions as
      CallDirection[] | string | undefined
    const directions = Array.isArray(rawDirections)
      ? rawDirections
      : typeof rawDirections === 'string'
        ? rawDirections.split(',')
        : undefined
    if (directions?.length === 1) {
      const dir = directions[0]
      if (dir === 'inbound' || dir === 'outbound') {
        params.direction = dir
      }
    }

    if (filters.is_missed !== undefined) {
      params.answered = !filters.is_missed
    }

    if (filters.has_voicemail !== undefined) {
      params.hasVoicemail = Boolean(filters.has_voicemail)
    }

    startExport(params)
  }, [isExporting, startExport, state, filters])

  const isDisabled =
    !state.dateTimeRange?.after ||
    !state.dateTimeRange?.before ||
    status === 'completed' ||
    hasUnsupportedFilters

  const tooltipContent = hasUnsupportedFilters
    ? `${t('calls.export.unsupportedFiltersHeader')} ${unsupportedFilterNames.join(', ')}`
    : undefined

  return (
    <Tooltip content={tooltipContent} disabled={!hasUnsupportedFilters}>
      <Button
        alignSelf="center"
        onClick={handleClick}
        disabled={isDisabled || isExporting}
        aria-label={t('calls.export.button')}
        display="flex"
        height="40px"
        minWidth="40px"
        px="16px"
        py="2px"
        justifyContent="center"
        alignItems="center"
        gap="8px"
        borderRadius="4px"
        bg="brand.solid"
        color="brand.contrast"
        _hover={{ bg: 'brand.solid' }}
        _active={{ bg: 'brand.solid' }}
        _disabled={{
          bg: 'brand.solid',
          color: 'brand.contrast',
          opacity: 0.5,
        }}
      >
        {isExporting ? (
          <Spinner size="xs" color="brand.contrast" />
        ) : (
          <Box
            as={DownloadIcon}
            width="16px"
            height="16px"
            color="brand.contrast"
          />
        )}
        <Text {...textStyles[textStyleKeys.Body3_Bold]} color="brand.contrast">
          {t(statusLabelKey[status])}
        </Text>
      </Button>
    </Tooltip>
  )
}
